import { IonContent, IonIcon, IonModal, useIonRouter } from "@ionic/react";
import {
  chevronForwardOutline,
  closeCircleOutline,
  searchOutline,
  sendOutline,
} from "ionicons/icons";
import React, { useMemo, useState } from "react";
import useAuth from "../../../hooks/useAuth";
import { Following } from "../../../pages/settings/datasharing/DataSharingTypes";
import EmptyState from "../../../pages/settings/datasharing/EmptyState";
import Avatar from "../Avatar";
import { ProfileDisplay, resolveProfileDisplay } from "../../../utils/helpers";
import AllFollowingListDesktop from "./AllFollowingListDesktop";

interface AllFollowingListProps {
  following: Following[];
  isOpen?: boolean;
  onClose?: () => void;
  defaultValue?: { label: string; value: number | string };
  onSelect?: (selected: any) => void;
  showMeOption?: boolean;
}

const AllFollowingList: React.FC<AllFollowingListProps> = ({
  following,
  isOpen,
  onClose,
  defaultValue = {},
  onSelect,
  showMeOption = true,
}) => {
  const [isFollowingModalOpen, setIsOpen] = React.useState(false);
  const [searchText, setSearchText] = React.useState("");

  // Resolve once per follower. Search, sort, the row and the selected label
  // all read this, so what you type, what you see and what you pick agree.
  // follower.name can't be used: mapFollowers pre-fills it with the email,
  // phone or "User N", none of which is what the row renders.
  const resolvedFollowers = useMemo(
    () =>
      following.map((follower: Following) => ({
        follower,
        profile: resolveProfileDisplay({
          firstName: follower.firstName,
          lastName: follower.lastName,
          email: follower.email,
          phone: follower.phone,
          avatar: follower.avatar,
        }),
      })),
    [following]
  );

  const filteredFollowers = resolvedFollowers.filter(({ profile }) =>
    profile.name.toLowerCase().includes(searchText.toLowerCase())
  );

  const [selected, setSelected] = useState<{
    label?: string;
    value?: number | string;
  }>(defaultValue || {});
  // Use isDesktop from useAuth hook (consistent with rest of the project)
  const { user, isDesktop } = useAuth();

  // The "Me" row is a sentinel the header compares against, so its label stays
  // literal - but its picture and initials come from the resolver like any other.
  const meProfile = useMemo(
    () =>
      resolveProfileDisplay({
        firstName: user?.first_name,
        lastName: user?.last_name,
        email: user?.email,
        phone: user?.phone,
        avatar: user?.avatar,
      }),
    [user]
  );
  const handleSelect = (follower: Following, profile: ProfileDisplay) => {
    const option = {
      label:
        profile.name.length > 20
          ? profile.name.slice(0, 20) + "..."
          : profile.name,
      value: follower.id,
    };

    setSelected(option);
    setIsOpen(false);
    onClose && onClose();

    if (onSelect) onSelect(option);
  };

  const closeFollowingModal = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  const router = useIonRouter();
  const followersAvailable = filteredFollowers.length > 0;

  // ==================== DESKTOP LAYOUT ====================
  if (isDesktop) {
    return (
      <AllFollowingListDesktop
        following={following}
        isOpen={isOpen}
        onClose={onClose}
        onSelect={onSelect}
        showMeOption={showMeOption}
        user={user}
      />
    );
  }

  // ==================== MOBILE LAYOUT ====================
  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      breakpoints={[0, 0.5, 0.8]}
      initialBreakpoint={0.8}
      handleBehavior="cycle"
      style={{
        "--border-radius": "16px 16px 0 0",
      }}
    >
      <IonContent
        style={{
          "--background": "white",
          borderRadius: "20px",
          width: "100%",
          height: "100%",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: "20px 20px 0",
            position: "relative",
            textAlign: "center",
          }}
        >
          <h2
            style={{
              fontSize: "16px",
              fontWeight: "600",
              color: "#000",
              margin: "0 0 10px",
            }}
          >
            Following
          </h2>
          <IonIcon
            onClick={() => closeFollowingModal()}
            style={{
              position: "absolute",
              top: "15px",
              left: "20px",
              fontSize: "18px",
              color: "#05ACD9",
              cursor: "pointer",
              width: "26px",
              height: "26px",
            }}
            icon={closeCircleOutline}
          />
          {followersAvailable && (
            <p style={{ color: "#666", fontSize: "14px", marginBottom: "10px" }}>
              Select user to view their data
            </p>
          )}
        </div>

        {/* Search Bar */}
        {filteredFollowers.length > 0 && (
          <div style={{ position: "relative", padding: "0 20px 20px" }}>
            <input
              type="text"
              placeholder="Search"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{
                backgroundColor: "#F5F5F5",
                borderRadius: "12px",
                border: "none",
                padding: "12px 40px 12px 16px",
                width: "100%",
                fontSize: "16px",
                color: "#333",
                outline: "none",
              }}
            />
            <IonIcon
              icon={searchOutline}
              style={{
                position: "absolute",
                right: "35px",
                top: "35%",
                transform: "translateY(-50%)",
                fontSize: "20px",
                color: "#888",
                pointerEvents: "none",
              }}
            />
          </div>
        )}

        {/* Followers List */}
        {followersAvailable ? (
          <div style={{ flex: 1, display: "flex", flexDirection: "column" }}>
            <div style={{ overflowY: "auto" }}>
              {filteredFollowers
                .slice()
                .sort((a, b) =>
                  a.profile.name
                    .toLowerCase()
                    .localeCompare(b.profile.name.toLowerCase())
                )
                .map(({ follower, profile }) => {
                  return (
                  <div
                    key={follower.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "6px 20px",
                      cursor: "pointer",
                      gap: "15px",
                    }}
                    onClick={() => handleSelect(follower, profile)}
                  >
                    <div>
                      <Avatar
                        initials={profile.initials}
                        imageSrc={profile.avatar}
                        imageClassName="rounded-[12px] border"
                      />
                    </div>

                    <div style={{ flex: 1 }}>
                      <div
                        className="truncate"
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: "#000",
                          maxWidth: "150px",
                        }}
                        title={profile.name}
                      >
                        {profile.name}
                      </div>
                    </div>

                    <IonIcon
                      style={{
                        color: "#586670",
                        fontSize: "16px",
                        marginLeft: "10px",
                      }}
                      icon={chevronForwardOutline}
                    />
                  </div>
                  );
                })}
            </div>

            {/* Footer - Only Me Option */}
            {showMeOption && (
              <div
                style={{
                  padding: "10px 20px",
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  borderTop: "2px solid #eee",
                }}
                onClick={() =>
                  handleSelect(
                    {
                      id: user?.profile_id || 0,
                      name: "Me",
                      avatar: meProfile.avatar,
                      initials: meProfile.initials,
                    },
                    { ...meProfile, name: "Me" }
                  )
                }
              >
                <div style={{ marginRight: "15px" }}>
                  <Avatar
                    initials={meProfile.initials}
                    imageSrc={meProfile.avatar}
                    imageClassName="rounded-[12px] border"
                  />
                </div>

                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#000",
                    }}
                  >
                    Me
                  </div>
                </div>

                <IonIcon
                  style={{
                    color: "#586670",
                    fontSize: "16px",
                    marginLeft: "10px",
                  }}
                  icon={chevronForwardOutline}
                />
              </div>
            )}
          </div>
        ) : (
          <div className="mx-3">
            <EmptyState
              title="Follow an Arc Connect user"
              description="Send a request to another person to view their therapy data."
              buttonText="Send Request"
              onButtonClick={() =>
                router.push("/settings/following?openSharing=true")
              }
              icon={sendOutline}
            />
          </div>
        )}
      </IonContent>
    </IonModal>
  );
};

export default AllFollowingList;
